import { createTrackingEmitterHooks } from "@b2bportal/core-tracking";
import type { QuoteErrorType, TrackingProperties } from "@b2bportal/core-types";

export type QuotedProductTrackingProperties = {
  local_currency_code: string;
  daily_rate_user_currency: number;
  sell_rate_user_currency: number;
  owed_later_excluding_taxes_fees_user_currency: number;
  owed_later_including_taxes_fees_user_currency: number;
  owed_now_excluding_taxes_fees_user_currency: number;
  owed_now_including_taxes_fees_user_currency: number;
  owed_now_taxes_fees_user_currency: number;
  owed_overall_excluding_taxes_fees_user_currency: number;
  owed_overall_including_taxes_fees_user_currency: number;
};

export enum PaymentMethodTrackingEnum {
  CARD = "CARD",
  PAYPAL = "PAYPAL",
}
export type PaymentMethodTrackingProperties = {
  payment_type: PaymentMethodTrackingEnum;
};

export type CheckoutTrackingProperties = {
  checkoutQuotedProduct: QuotedProductTrackingProperties;
  checkoutPaymentMethod: PaymentMethodTrackingProperties;
  checkoutCartFulfillFailureReason: string;
  checkoutPurchaseCompletedTrackingProperties: TrackingProperties;
};

export const fallbackCheckoutQuotedProductTrackingProperties = {
  local_currency_code: "",
  daily_rate_user_currency: 0,
  sell_rate_user_currency: 0,
  owed_later_excluding_taxes_fees_user_currency: 0,
  owed_later_including_taxes_fees_user_currency: 0,
  owed_now_excluding_taxes_fees_user_currency: 0,
  owed_now_including_taxes_fees_user_currency: 0,
  owed_now_taxes_fees_user_currency: 0,
  owed_overall_excluding_taxes_fees_user_currency: 0,
  owed_overall_including_taxes_fees_user_currency: 0,
};

export const fallbackCheckoutPaymentMethodTrackingProperties = {
  payment_type: PaymentMethodTrackingEnum.CARD,
};

export const fallbackPurchaseCompletedTrackingProperties: TrackingProperties = {
  properties: {},
};

/** Checkout amplitude event names correspond to the events that are sent to Amplitude
 * with the same name accross verticals.
 * If the event has a different naming according to the vertical i.e. hotel_view_travelers
 * It should be added the right vertical types definition. In this case it should be added
 * to the HotelCheckoutEventName enum
 */

export enum CheckoutEventName {
  viewed_contact_info = "viewed_contact_info",
  view_payment = "view_payment",
  modal_alert = "modal_alert",
  add_contact_info = "add_contact_info",
}

/** Checkout raw events are common across different verticals. Each vertical checkout transformer
 * should handle the right transformation for this events
 */

export enum CheckoutRawEventName {
  CheckoutContactInformationEnabled = "CheckoutContactInformationEnabled",
  CheckoutFulfillPurchaseButtonEnabled = "CheckoutFulfillPurchaseButtonEnabled",
  CheckoutPaymentSectionEnabled = "CheckoutPaymentSectionEnabled",
  CheckoutTravelersEnabled = "CheckoutTravelersEnabled",
  CheckoutQuoteErrorDisplayed = "CheckoutQuoteErrorDisplayed",
  SubmitContactInformationButtonClicked = "SubmitContactInformationButtonClicked",
}

export type CheckoutContactInformationEnabledRawEvent = {
  name: CheckoutRawEventName.CheckoutContactInformationEnabled;
};

export type CheckoutFulfillPurchaseButtonEnabledRawEvent = {
  name: CheckoutRawEventName.CheckoutFulfillPurchaseButtonEnabled;
};

export type CheckoutTravelersEnabledRawEvent = {
  name: CheckoutRawEventName.CheckoutTravelersEnabled;
};
export type CheckoutPaymentSectionEnabledRawEvent = {
  name: CheckoutRawEventName.CheckoutPaymentSectionEnabled;
};

export type CheckoutQuoteErrorDisplayedRawEvent = {
  name: CheckoutRawEventName.CheckoutQuoteErrorDisplayed;
  properties: {
    error: {
      type: QuoteErrorType;
    };
  };
};

export type SubmitContactInformationButtonClickedRawEvent = {
  name: CheckoutRawEventName.SubmitContactInformationButtonClicked;
};

export type AddContactInformationHTSEvent = {
  name: CheckoutEventName.add_contact_info;
  properties: {};
};

export type CheckoutRawEvent =
  | CheckoutContactInformationEnabledRawEvent
  | CheckoutTravelersEnabledRawEvent
  | CheckoutPaymentSectionEnabledRawEvent
  | SubmitContactInformationButtonClickedRawEvent
  | CheckoutQuoteErrorDisplayedRawEvent
  | (CheckoutFulfillPurchaseButtonEnabledRawEvent & {
      properties?: Record<string, unknown>;
    });

export type CheckoutHTSEvent = AddContactInformationHTSEvent;

export const {
  useEmitRawEvent: useEmitCoreCheckoutRawEvent,
  useEmitRawEventOnce: useEmitCoreCheckoutRawEventOnce,
} = createTrackingEmitterHooks<CheckoutRawEvent>();
